import { HttpClient } from '@angular/common/http';
import { Component, inject } from '@angular/core';
import { GetRowIdParams, GridReadyEvent } from '@ag-grid-community/core';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import _ from 'lodash';
import { AgFns, GridOptionsExt } from 'app/shared/ag-fns';
import { DomainBaseComponent } from 'app/shared/domain-base.component';
import { DomainService } from 'app/shared/domain.service';
import { AgStyleFns } from 'app/shared/ag-style-fns';
import { InvoiceDialogComponent } from 'app/shared/invoice-dialog.component';
import { UtilFns } from 'app/shared/util-fns';
import { environment } from 'environments/environment';
import { firstValueFrom } from 'rxjs';

// The server serializes PascalCase -- AddNewtonsoftJson is configured with no camelCasing
// (Startup.cs). These names must match the C# properties exactly or every field reads undefined.
interface QbAuditDocument {
  DocType: string;
  AccountName: string;
  Status: string;
  DocNumber: string;
  LocalId: string;
  QbId: string;
  LocalAmt: number | null;
  QbAmt: number | null;
  PostedTs: string | null;
  Detail: string;
}

interface QbAuditTypeSummary {
  DocType: string;
  LocalCount: number;
  QbCount: number;
  ProblemCount: number;
  LocalTotal: number;
  QbTotal: number;
  Difference: number;
}

interface QbAuditResult {
  StartDate: string;
  EndDate: string;
  Documents: QbAuditDocument[];
  Summaries: QbAuditTypeSummary[];
  ProblemCount: number;
  Notes: string[];
}

const STATUS_LABELS: Record<string, string> = {
  OK: 'Agrees',
  MissingInQb: 'Not in QuickBooks',
  MissingInUniformax: 'Not in Uniformax',
  AmountMismatch: 'Amount is different',
  VoidedOrZeroedInQb: 'Zero in QuickBooks',
  DuplicateInQb: 'Duplicate in QuickBooks',
  IdMismatch: 'Points at a different document',
  DoubleSubmission: 'Submitted two times',
};

@Component({
  selector: 'app-qb-audit',
  templateUrl: './qb-audit.component.html',
  standalone: false,
})
export class QbAuditComponent extends DomainBaseComponent {
  private http = inject(HttpClient);

  gridOptions: GridOptionsExt;
  documents: QbAuditDocument[] = [];
  result: QbAuditResult | null = null;
  isLoading = false;
  problemsOnly = false;

  startDate: Date;
  endDate: Date;

  constructor(protected domainService: DomainService) {
    super(domainService);

    const now = new Date();
    this.startDate = new Date(now.getFullYear(), now.getMonth(), 1);
    this.endDate = now;

    this.gridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onGridReady,
      getRowId: (params: GetRowIdParams) => {
        const d = params.data as QbAuditDocument;
        return `${d.DocType}|${d.DocNumber}|${d.QbId ?? ''}`;
      },
      getRowClass: (p: any) => (p.data?.Status && p.data.Status !== 'OK' ? 'qb-audit-problem' : ''),
    });
    this.isPageReady = true;
  }

  onGridReady(event: GridReadyEvent) {
    const colDefs = [
      { headerName: 'Type', field: 'DocType', filter: 'agSetColumnFilter', ...AgFns.fixWidth(120) },
      {
        headerName: 'Result',
        field: 'Status',
        filter: 'agSetColumnFilter',
        ...AgFns.fixWidth(200),
        valueFormatter: (p: any) => STATUS_LABELS[p.value] ?? p.value,
      },
      {
        headerName: 'Posted',
        field: 'PostedTs',
        filter: 'agDateColumnFilter',
        ...AgFns.fixWidth(160),
        valueFormatter: (p: any) => (p.value ? new Date(p.value).toLocaleString() : ''),
      },
      { headerName: 'Account', field: 'AccountName', filter: 'agTextColumnFilter', ...AgFns.flexWidth(180) },
      {
        headerName: 'Document',
        field: 'DocNumber',
        filter: 'agTextColumnFilter',
        ...AgFns.fixWidth(150),
        cellClass: (p: any) => (this.getInvoiceId(p.data) ? AgStyleFns.getLinkCellClass() : ''),
        onCellClicked: (e: any) => this.showInvoice(e.data as QbAuditDocument),
      },
      { headerName: 'QuickBooks Id', field: 'QbId', filter: 'agTextColumnFilter', ...AgFns.fixWidth(130) },
      {
        headerName: 'Uniformax Amt',
        field: 'LocalAmt',
        filter: 'agNumberColumnFilter',
        ...AgFns.fixWidth(130),
        valueFormatter: (p: any) => (p.value == null ? '' : UtilFns.fmtCurrency(p.value)),
        type: 'numericColumn',
      },
      {
        headerName: 'QuickBooks Amt',
        field: 'QbAmt',
        filter: 'agNumberColumnFilter',
        ...AgFns.fixWidth(140),
        valueFormatter: (p: any) => (p.value == null ? '' : UtilFns.fmtCurrency(p.value)),
        type: 'numericColumn',
      },
      {
        headerName: 'Difference',
        colId: 'difference',
        filter: 'agNumberColumnFilter',
        ...AgFns.fixWidth(120),
        type: 'numericColumn',
        valueGetter: (p: any) => {
          const d = p.data as QbAuditDocument;
          if (d?.LocalAmt == null || d?.QbAmt == null) return null;
          return d.QbAmt - d.LocalAmt;
        },
        valueFormatter: (p: any) => (p.value == null ? '' : UtilFns.fmtCurrency(p.value)),
      },
      { headerName: 'What it means', field: 'Detail', filter: 'agTextColumnFilter', tooltipField: 'Detail', ...AgFns.flexWidth(300) },
    ];
    AgFns.initGrid(event, colDefs, [{ colId: 'DocType', sort: 'asc' }]);
  }

  async runAudit() {
    if (!this.startDate || !this.endDate) {
      await this.dialogService.showOkMessage('No Dates', 'Select a start date and an end date.');
      return;
    }
    if (this.startDate > this.endDate) {
      await this.dialogService.showOkMessage('Wrong Dates', 'The start date must be before the end date.');
      return;
    }

    this.isLoading = true;
    try {
      const start = this.toQueryDate(this.startDate);
      const end = this.toQueryDate(this.endDate);
      const url = `${environment.apiRoot}api/quickbooks/RunQbAudit?startDate=${start}&endDate=${end}`;
      const result = await firstValueFrom(this.http.get<QbAuditResult>(url));
      this.result = result;
      this.applyFilter();

      const total = result.Documents?.length ?? 0;
      if (total === 0) {
        this.toastr.warning('The system found no documents in this period. Check the dates.', 'QuickBooks Audit');
      } else if (result.ProblemCount === 0) {
        this.toastr.success(`The system compared ${total} documents. All of them agree.`, 'QuickBooks Audit');
      } else {
        this.toastr.warning(`The system compared ${total} documents. ${result.ProblemCount} do not agree.`, 'QuickBooks Audit');
      }
    } catch (err: any) {
      this.toastr.error(err.message || 'The audit did not finish.', 'QuickBooks Audit');
    } finally {
      this.isLoading = false;
    }
  }

  /// Only Invoice and Payment rows point at an InvoiceHeader. A credit memo row carries a CreditMemo id
  /// and a sales receipt row a JoHeader id, so neither opens the invoice dialog.
  getInvoiceId(d: QbAuditDocument): string | null {
    if (!d?.LocalId) return null;
    return d.DocType === 'Invoice' || d.DocType === 'Payment' ? d.LocalId : null;
  }

  async showInvoice(d: QbAuditDocument) {
    const invoiceId = this.getInvoiceId(d);
    if (!invoiceId) return;
    await InvoiceDialogComponent.openDialog(this.pngDialogService, { invoiceId });
  }

  applyFilter() {
    const all = this.result?.Documents ?? [];
    this.documents = this.problemsOnly ? all.filter((d) => d.Status !== 'OK') : all;
  }

  /// Local date parts, not toISOString -- that shifts the day backwards west of UTC.
  private toQueryDate(d: Date) {
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${mm}-${dd}`;
  }

  async exportToExcel() {
    if (!this.result) return;

    const workbook = new ExcelJS.Workbook();
    const exportDate = new Date();
    const period = `${this.toQueryDate(this.startDate)} to ${this.toQueryDate(this.endDate)}`;

    // Sheet 1 -- the tie-out totals, which is what gets compared against a QuickBooks report.
    const tieOut = workbook.addWorksheet('Tie-Out');
    tieOut.addRow([`QuickBooks Audit — ${period}`]);
    tieOut.getRow(1).font = { bold: true, size: 14 };
    tieOut.mergeCells('A1:G1');
    tieOut.addRow([`Exported: ${exportDate.toLocaleDateString()} ${exportDate.toLocaleTimeString()}`]);
    tieOut.getRow(2).font = { italic: true, size: 10 };
    tieOut.mergeCells('A2:G2');
    tieOut.addRow([]);

    tieOut.addRow([
      'Document Type',
      'Uniformax Count',
      'QuickBooks Count',
      'Uniformax Total',
      'QuickBooks Total',
      'Difference',
      'Do Not Agree',
    ]);
    const tieHeader = tieOut.getRow(4);
    tieHeader.font = { bold: true };
    tieHeader.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0E0E0' } };
    [22, 18, 18, 18, 18, 15, 15].forEach((w, i) => (tieOut.getColumn(i + 1).width = w));

    this.summaries.forEach((s) => {
      tieOut.addRow([s.DocType, s.LocalCount, s.QbCount, s.LocalTotal, s.QbTotal, s.Difference, s.ProblemCount]);
    });
    if (this.summaries.length > 1) {
      const totalRow = tieOut.addRow([
        'All types',
        '',
        '',
        this.grandLocalTotal,
        this.grandQbTotal,
        this.grandQbTotal - this.grandLocalTotal,
        this.result.ProblemCount,
      ]);
      totalRow.font = { bold: true };
    }
    for (let row = 5; row <= tieOut.rowCount; row++) {
      ['D', 'E', 'F'].forEach((col) => (tieOut.getCell(`${col}${row}`).numFmt = '$#,##0.00'));
    }

    // Sheet 2 -- every document compared, so the totals above can be traced to their parts.
    const detail = workbook.addWorksheet('Documents');
    detail.addRow([
      'Type',
      'Result',
      'Account',
      'Document',
      'Uniformax Id',
      'QuickBooks Id',
      'Uniformax Amt',
      'QuickBooks Amt',
      'Difference',
      'Posted',
      'What it means',
    ]);
    const detailHeader = detail.getRow(1);
    detailHeader.font = { bold: true };
    detailHeader.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0E0E0' } };
    [14, 28, 30, 18, 16, 14, 16, 16, 14, 20, 70].forEach((w, i) => (detail.getColumn(i + 1).width = w));

    (this.result.Documents ?? []).forEach((d) => {
      const diff = d.LocalAmt == null || d.QbAmt == null ? null : d.QbAmt - d.LocalAmt;
      detail.addRow([
        d.DocType,
        STATUS_LABELS[d.Status] ?? d.Status,
        d.AccountName ?? '',
        d.DocNumber,
        d.LocalId ?? '',
        d.QbId ?? '',
        d.LocalAmt,
        d.QbAmt,
        diff,
        d.PostedTs ? new Date(d.PostedTs).toLocaleString() : '',
        d.Detail,
      ]);
    });
    for (let row = 2; row <= detail.rowCount; row++) {
      ['G', 'H', 'I'].forEach((col) => (detail.getCell(`${col}${row}`).numFmt = '$#,##0.00'));
    }
    detail.autoFilter = { from: 'A1', to: { row: 1, column: 11 } };

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `QuickBooks_Audit_${this.toQueryDate(this.startDate)}_to_${this.toQueryDate(this.endDate)}.xlsx`);
  }

  get canExport(): boolean {
    return !this.isLoading && (this.result?.Documents?.length ?? 0) > 0;
  }

  get summaries(): QbAuditTypeSummary[] {
    return this.result?.Summaries ?? [];
  }

  get grandLocalTotal(): number {
    return _.sumBy(this.summaries, (s) => s.LocalTotal);
  }

  get grandQbTotal(): number {
    return _.sumBy(this.summaries, (s) => s.QbTotal);
  }
}
